/**
 * CToast — the dark gradient toast banner from Figma (1169:149380). Purely
 * presentational: one alert with a status icon badge, a bold title, an optional
 * sub-text, and a dismiss button. State/queueing lives in `AlertProvider`, which
 * renders one `CToast` per active alert.
 */
import { Info, X } from "lucide-react";
import type * as React from "react";
import ErrorIcon from "@/assets/images/toast/toast-error.svg";
import SuccessIcon from "@/assets/images/toast/toast-success.svg";
import WarningIcon from "@/assets/images/toast/toast-warning.svg";
import { cn } from "@/lib/utils";

/** The visual/semantic flavour of a toast. */
export type CToastType = "success" | "warning" | "error" | "info";

export interface CToastProps {
  /** Which status flavour to render (drives gradient, icon, and ring colour). */
  type: CToastType;
  /** Bold headline — maps to the backend `message`. */
  title: string;
  /** Optional sub-text — maps to humanised `details` (newlines render as lines). */
  description?: string;
  /** Called when the dismiss button is clicked. */
  onClose?: () => void;
}

interface VariantStyle {
  /** Icon-badge inner glyph (two-tone SVG for status, lucide for info). */
  Icon: React.ComponentType<{ className?: string }>;
  /** `bg-gradient-to-r` from/to stops for the banner surface. */
  gradient: string;
  /** Icon-badge ring border colour. */
  ring: string;
}

// Per-variant styling as a plain lookup record (the Shared layer avoids `cva`).
const VARIANTS: Record<CToastType, VariantStyle> = {
  success: {
    Icon: SuccessIcon,
    gradient: "from-toast-success-from to-toast-success-to",
    ring: "border-toast-success-ring",
  },
  warning: {
    Icon: WarningIcon,
    gradient: "from-toast-warning-from to-toast-warning-to",
    ring: "border-toast-warning-ring",
  },
  error: {
    Icon: ErrorIcon,
    gradient: "from-toast-error-from to-toast-error-to",
    ring: "border-toast-error-ring",
  },
  info: {
    Icon: Info,
    gradient: "from-toast-info-from to-toast-info-to",
    ring: "border-toast-info-ring",
  },
};

/**
 * Render a single toast banner.
 *
 * @param type which status flavour to show
 * @param title the bold headline
 * @param description optional sub-text (supports `\n` for stacked field errors)
 * @param onClose dismiss handler
 */
const CToast = ({ type, title, description, onClose }: CToastProps) => {
  const { Icon, gradient, ring } = VARIANTS[type];

  return (
    <div
      // Banner surface: width grows with content between 420px and 580px,
      // left→right gradient (stops at 1.3%→50% per the design), hairline edge +
      // soft shadow.
      className={cn(
        "flex min-w-[420px] max-w-[580px] items-start justify-between rounded-lg border border-white/10 bg-gradient-to-r from-[1.3%] to-[50%] py-3.5 pr-2.5 pl-4 shadow-[0_0_1px_rgba(145,158,171,0.24),-20px_20px_20px_rgba(145,158,171,0.24)]",
        gradient,
      )}
      role="alert"
    >
      <div className="flex items-center gap-2.5">
        {/* Icon badge: translucent disc with a status-coloured ring. */}
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full border-[1.067px] bg-white/20 p-2",
            ring,
          )}
        >
          <Icon className="size-4" />
        </span>

        <div className="flex flex-col gap-0.5">
          <p className="label-md tracking-[-0.176px] break-words text-white">
            {title}
          </p>
          {/* Sub-text wraps; `\n`-joined field errors render as separate lines. */}
          {description && (
            <p className="paragraph-xs whitespace-pre-line break-words text-toast-subtext">
              {description}
            </p>
          )}
        </div>
      </div>

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="shrink-0 text-white/80 transition-colors hover:text-white"
        >
          <X className="size-5" />
        </button>
      )}
    </div>
  );
};

export default CToast;
export { CToast };
